import { useMemo, useState } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { autoUpdate, offset, useClick, useFloating, useInteractions } from '@floating-ui/react';
import { SPACE_200 } from '@alltrails/denali/tokens';
import debounce from 'lodash.debounce';
import useStyleKeyboardShortcuts from '../../hooks/useStyleKeyboardShortcuts';
import { useDispatch, useSelector } from '../../redux';
import { updateAdminStyleSettings } from '../../redux/reducer';
import { BoundaryFilterType, StyleId } from '../../types/Styles';
import useStyleSelection from '../../hooks/useStyleSelection';
import LayerMenu from '../LayerMenu';
import LayerButton from '../LayerButton';
import TerrainButton from '../TerrainButton';
import { HandleStyleRoadblock } from '../../types/Roadblock';

type StyleControlsProps = {
  handleRoadblock: HandleStyleRoadblock;
  on3dButtonTapped?: () => void;
  onStyleModalChange?: (isOpen: boolean) => void;
} & Parameters<typeof useStyleSelection>[0];

/**
 * @deprecated Use new button components used by MapControls
 */
const StyleControls = ({
  on3dButtonTapped,
  onStyleModalChange,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleFailedEntitlementCheck,
  handleRoadblock
}: StyleControlsProps) => {
  const dispatch = useDispatch();
  const { hasPermission } = useAuthorization();

  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const { adminStyleSettings } = useSelector(state => ({
    adminStyleSettings: state.map.adminStyleSettings
  }));

  const { onStyleSelect } = useStyleSelection({
    onMapBaseLayerTapped,
    onMapOverlayTapped,
    disableAnalytics,
    handleFailedEntitlementCheck,
    handleRoadblock
  });

  const handleStyleSelectWithDefaults = (styleId: StyleId) => {
    if (isAdmin && styleId === 'geoBoundaries' && (!adminStyleSettings?.boundaryFilters || adminStyleSettings.boundaryFilters.length === 0)) {
      dispatch(
        updateAdminStyleSettings({
          ...adminStyleSettings,
          boundaryFilters: [BoundaryFilterType.alertClosure, BoundaryFilterType.alertNonClosure]
        })
      );
    }

    onStyleSelect(styleId);
  };

  useStyleKeyboardShortcuts(isAdmin ? handleStyleSelectWithDefaults : undefined);

  const [isMapSelectionModalOpen, setIsMapSelectionModalOpen] = useState(false);

  // Debounced to handle race conditions when clicking on the layer button while the layer menu is already open.
  // This can close the menu via focus trap deactivation before the button has a chance to handle the click, which opens it again.
  const setModalOpenStatus = useMemo(
    () =>
      debounce((isOpen: boolean) => {
        onStyleModalChange?.(isOpen);
        setIsMapSelectionModalOpen(isOpen);
      }, 50),
    [onStyleModalChange]
  );

  const { refs, floatingStyles, context } = useFloating({
    placement: 'left-start',
    middleware: [offset({ mainAxis: SPACE_200, crossAxis: 0 })],
    whileElementsMounted: autoUpdate
  });

  const click = useClick(context);

  const { getFloatingProps } = useInteractions([click]);

  return (
    <>
      <LayerButton ref={refs.setReference} isOpen={isMapSelectionModalOpen} onClick={setModalOpenStatus} />
      <TerrainButton onClick={() => on3dButtonTapped?.()} />
      {isMapSelectionModalOpen && (
        <LayerMenu
          ref={refs.setFloating}
          isOpen={isMapSelectionModalOpen}
          mapLocation="trails manage page" // This component is deprecated and only used in portal and geo-contribute
          onCloseRequest={() => setModalOpenStatus(false)}
          onMapBaseLayerTapped={onMapBaseLayerTapped}
          onMapOverlayTapped={onMapOverlayTapped}
          disableAnalytics={disableAnalytics}
          handleFailedEntitlementCheck={handleFailedEntitlementCheck}
          handleRoadblock={handleRoadblock}
          desktopModalProps={{ ...getFloatingProps(), style: floatingStyles }}
        />
      )}
    </>
  );
};

export default StyleControls;
